import { useEffect, useState } from "react";
import { getPrize } from "./data.js";
import { CLINIC_ID, supabase } from "./supabase.js";
import { dateLabel } from "./lib/format.js";
import { RefreshCw, Search } from "lucide-react";

const STATUS_CONFIG = {
  sent:      { label: "Terkirim",        bg: "#eff6ff", color: "#2563eb" },
  opened:    { label: "Dibuka",          bg: "#fefce8", color: "#ca8a04" },
  submitted: { label: "Diisi",           bg: "#f0fdf4", color: "#16a34a" },
  used:      { label: "Voucher Dipakai", bg: "#faf5ff", color: "#7c3aed" },
  expired:   { label: "Kadaluwarsa",     bg: "#f9fafb", color: "#9ca3af" },
};

function rowStatus(row) {
  if (row.voucher_used) return "used";
  if (row.submitted_at) return "submitted";
  if (row.voucher_code && new Date(row.expires_at) < new Date()) return "expired";
  if (row.opened_at)    return "opened";
  return "sent";
}

function StarBar({ rating }) {
  if (!rating) return <span style={{ color: "#d1d5db" }}>—</span>;
  return (
    <span style={{ color: "#f59e0b", fontSize: 14, letterSpacing: 1 }}>
      {"★".repeat(rating)}{"☆".repeat(5 - rating)}
      <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 4 }}>{rating}/5</span>
    </span>
  );
}

export function SurveyDashboard() {
  const [rows, setRows]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [search, setSearch]   = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase
        .from("survey_tokens")
        .select("id,token,client_name,client_phone,created_at,expires_at,submitted_at,opened_at,rating,comment,voucher_code,voucher_used,voucher_used_at,prize_type,visit_id")
        .eq("clinic_id", CLINIC_ID)
        .order("created_at", { ascending: false })
        .limit(200);
      if (err) throw err;
      setRows(data || []);
    } catch (e) {
      setError(e.message || "Gagal memuat data survey.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = (rows || []).filter((r) => {
    const s = rowStatus(r);
    if (filterStatus !== "all" && s !== filterStatus) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        (r.client_name || "").toLowerCase().includes(q) ||
        (r.voucher_code || "").toLowerCase().includes(q) ||
        (r.client_phone || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Stats
  const stats = (rows || []).reduce((acc, r) => {
    const s = rowStatus(r);
    acc[s] = (acc[s] || 0) + 1;
    if (r.rating) { acc._ratingSum = (acc._ratingSum || 0) + r.rating; acc._ratingCount = (acc._ratingCount || 0) + 1; }
    return acc;
  }, {});
  const avgRating = stats._ratingCount ? (stats._ratingSum / stats._ratingCount).toFixed(1) : null;

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>Survey Kepuasan</h1>
          <p style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>Monitor pengiriman & respon survey pasien</p>
        </div>
        <button
          className="button button-soft"
          onClick={load}
          disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <RefreshCw size={15} className={loading ? "spin" : ""} />
          {loading ? "Memuat…" : "Refresh"}
        </button>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Stats cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { key: "all",      label: "Total Dikirim", val: rows?.length ?? "—",      bg: "#f8fafc", color: "#374151" },
          { key: "submitted",label: "Diisi",          val: stats.submitted ?? 0,    bg: "#f0fdf4", color: "#16a34a" },
          { key: "used",     label: "Voucher Dipakai",val: stats.used ?? 0,         bg: "#faf5ff", color: "#7c3aed" },
          { key: "expired",  label: "Kadaluwarsa",    val: stats.expired ?? 0,      bg: "#f9fafb", color: "#9ca3af" },
          { key: "_rating",  label: "Rating Rata-rata",val: avgRating ? `⭐ ${avgRating}` : "—", bg: "#fffbeb", color: "#d97706" },
        ].map(({ key, label, val, bg, color }) => (
          <div
            key={key}
            onClick={() => key !== "_rating" && setFilterStatus(filterStatus === key ? "all" : key)}
            style={{
              background: filterStatus === key ? color : bg,
              color: filterStatus === key ? "#fff" : color,
              borderRadius: 10, padding: "14px 16px",
              cursor: key !== "_rating" ? "pointer" : "default",
              border: `1px solid ${filterStatus === key ? color : "#e5e7eb"}`,
              transition: "all 0.15s",
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800 }}>{val}</div>
            <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2, opacity: 0.8 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 16 }}>
        <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari nama, nomor HP, atau kode voucher…"
          style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
        />
      </div>

      {/* Table */}
      {loading && !rows ? (
        <div style={{ textAlign: "center", color: "#9ca3af", padding: 40 }}>Memuat…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", color: "#9ca3af", padding: 40 }}>Tidak ada data.</div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #e5e7eb" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Pasien", "Dikirim", "Status", "Rating", "Hadiah", "Kode Voucher", "Voucher Dipakai"].map((h) => (
                  <th key={h} style={{ padding: "9px 12px", textAlign: "left", color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const s = rowStatus(r);
                const sc = STATUS_CONFIG[s];
                const prize = r.prize_type ? getPrize(r.prize_type) : null;
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ fontWeight: 600, color: "#111827" }}>{r.client_name || "—"}</div>
                      {r.client_phone && <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.client_phone}</div>}
                    </td>
                    <td style={{ padding: "10px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>
                      {r.created_at ? dateLabel(r.created_at, { time: true }) : "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ background: sc.bg, color: sc.color, borderRadius: 6, padding: "2px 8px", fontWeight: 600, fontSize: 11 }}>
                        {sc.label}
                      </span>
                      {r.submitted_at && (
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                          {dateLabel(r.submitted_at, { time: true })}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <StarBar rating={r.rating} />
                      {r.comment && (
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3, maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r.comment}>
                          "{r.comment}"
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {prize ? (
                        <span style={{ fontSize: 12 }}>{prize.emoji} {prize.label}</span>
                      ) : r.voucher_code ? (
                        <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
                      ) : "—"}
                    </td>
                    <td style={{ padding: "10px 12px", fontFamily: "monospace", fontWeight: 700, fontSize: 13 }}>
                      {r.voucher_code ? (
                        <span style={{ color: r.voucher_used ? "#9ca3af" : "#7c3aed", textDecoration: r.voucher_used ? "line-through" : "none" }}>
                          {r.voucher_code}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={{ padding: "10px 12px", color: "#6b7280", fontSize: 12 }}>
                      {r.voucher_used
                        ? <span style={{ color: "#7c3aed", fontWeight: 600 }}>✓ {r.voucher_used_at ? dateLabel(r.voucher_used_at, { time: true }) : "Dipakai"}</span>
                        : r.voucher_code ? <span style={{ color: "#d1d5db" }}>Belum dipakai</span>
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
